import {
  Create,
  Datagrid,
  Edit,
  FunctionField,
  Loading,
  List,
  SaveButton,
  Show,
  SimpleForm,
  SimpleList,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useRecordContext,
  useRedirect,
  useTranslate,
  TransformData
} from 'react-admin'
import log from 'loglevel'
import { includes, isEqual, isNil, split } from 'lodash/fp'
import { Grid, Typography, useMediaQuery } from '@mui/material'
import { Theme } from '@mui/material/styles'

import { Pagination, Title } from '../Util'
import { NotificationKind } from '../../types'
import { PartnerUserForA } from './types'
import { useNavigate } from 'react-router-dom'
import { assign } from 'lodash'
import { ElectricityUsageChart } from '../ElectricityUsage'

const UserEditToolbar: React.FC = () => (
  <Toolbar>
    <SaveButton label="mine.user.createSave" />
  </Toolbar>
)
const getHouseName = (name: string): string => {
  const names = split('#')(name)
  return names[1] as string
}
const UserEditForm: React.FC = (props) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const record: PartnerUserForA = useRecordContext()
  if (includes('#')(record.name)) {
    assign(record, { distName: record.name })
    record.name = getHouseName(record.name)
    record.spin = record.spin === 'null' ? '' : record.spin
  }
  return (
    <SimpleForm {...props} toolbar={<UserEditToolbar />}>
      <TextInput source="name" />
      <TextInput source="spin" fullWidth />
      <TextInput source="sms" fullWidth />
      <TextInput source="phone" fullWidth />
      {!isDesktop && <div style={{ marginBottom: '5em' }} />}
    </SimpleForm>
  )
}
const UserTitle: React.FC = () => {
  const record: PartnerUserForA = useRecordContext()
  const translate = useTranslate()
  if (isNil(record)) {
    return null
  }
  const title = `${translate('mine.users.houseShow')}: ${getHouseName(
    record.name
  )}`
  return <span>{title}</span>
}
const UserShowContent: React.FC<{ date: string }> = ({ date, ...props }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const record: PartnerUserForA = useRecordContext()
  if (isNil(record)) return <Loading />
  const spin = record.spin
  return (
    <SimpleShowLayout {...props}>
      <TextField source="spin" />
      {!isDesktop && <div style={{ marginBottom: '5em' }} />}
      <ElectricityUsageChart
        spin={spin}
        date={date}
        showTable={false}
        kind={NotificationKind.low}
      />
    </SimpleShowLayout>
  )
}

export const UserEdit: React.FC<{
  title?: string
  id: string
  resource: string
}> = ({ title, id, resource }) => {
  const navigate = useNavigate()
  const transform: TransformData = (
    data: PartnerUserForA,
    // @ts-expect-error
    { previousData }
  ): PartnerUserForA => {
    const { criterions, spin, name } = data
    data.criterionsOwn = !isEqual(criterions)(previousData.criterions)
    log.debug('[checkCriterions]', data)
    assign(data, { customerNumber: spin.substring(3, 16) })
    const names = split('#')(data.distName)
    assign(data, { name: `${names[0] as string}#${name}` })
    delete data.distName
    return data
  }
  const onSuccess = (): void => {
    navigate(-1)
  }
  return (
    <Edit
      id={id}
      resource={resource}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      transform={transform}
      title={<Title title={!isNil(title) ? title : 'mine.users.houseEdit'} />}>
      <UserEditForm />
    </Edit>
  )
}
export const UserList: React.FC<{
  title: string
  id: string
  resource: string
}> = ({ id, resource, title }) => {
  // const translate = useTranslate()
  // const redirect = useRedirect()
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  // const addHouse = (): void => {
  //   redirect(`/userCreate/${id}`)
  // }
  return (
    <Grid container justifyContent="center">
      <Grid item xs={12}>
        <List
          perPage={10}
          pagination={<Pagination />}
          actions={false}
          filter={{
            partner: id
          }}
          resource={resource}
          title={<Title title={title} />}>
          {isDesktop ? (
            <>
              <Pagination withPerPage />
              <Datagrid rowClick="edit" bulkActionButtons={false}>
                <FunctionField
                  source="name"
                  render={(record: PartnerUserForA) => {
                    const houseName = getHouseName(record.name)
                    return (
                      <Typography variant="inherit">{houseName}</Typography>
                    )
                  }}
                />
                <FunctionField
                  source="spin"
                  render={(record: PartnerUserForA) =>
                    record.spin === 'null' ? (
                      <></>
                    ) : (
                      <TextField source="spin" sortable={false} />
                    )
                  }
                />
              </Datagrid>
            </>
          ) : (
            <SimpleList primaryText={(record) => getHouseName(record.name)} />
          )}
        </List>
      </Grid>
      {/* <Grid item xs={5}>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          fullWidth
          onClick={addHouse}>
          {translate('mine.users.addHouse')}
        </Button>
      </Grid> */}
    </Grid>
  )
}
export const UserCreate: React.FC<{
  title: string
  id: string
  resource: string
}> = ({ id, resource, title }) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const redirect = useRedirect()
  const onSuccess = (): void => {
    redirect(`/usersList/${id}`)
  }
  return (
    <Create
      resource={resource}
      title={<Title title={title} />}
      mutationOptions={{ onSuccess }}>
      <SimpleForm>
        <TextInput source="houseName" fullWidth />
        <TextInput source="spin" fullWidth />
        <TextInput source="sms" fullWidth />
        {!isDesktop && <div style={{ marginBottom: '5em' }} />}
      </SimpleForm>
    </Create>
  )
}
export const UserShow: React.FC<{
  id: string
  resource: string
  date: string
}> = ({ id, resource, date }) => {
  return (
    <Show id={id} resource={resource} title={<UserTitle />}>
      <UserShowContent date={date} />
    </Show>
  )
}
